/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line jira/filename-case
import React, { useMemo, useEffect } from 'react';
import { css, jsx } from '@compiled/react';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import ComponentWithAnalytics from '@jira/platform__analytics-web-react/src/utils/component-with-analytics.tsx';
import BreadcrumbsApp from '@jira/navigation-apps__breadcrumbs/src/index.tsx';
import type { TenantContextSubscriber as TenantContextSubscriberType } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { useBreadcrumbs } from '../controller/index.tsx';
import type { BreadcrumbStateData } from '../types.tsx';

type Props = {
	TenantContextSubscriber: typeof TenantContextSubscriberType;
	onMount: () => void;
};

// Adapt to prop type from breadcrumbs app
const mapTrailToBreadcrumbItems = (trail: BreadcrumbStateData[]) =>
	trail.map((crumb) => ({
		...crumb,
		target: crumb.target || '_self',
	}));

const BreadcrumbRenderer = ({ onMount }: Props) => {
	const [{ trail }] = useBreadcrumbs();

	const items = useMemo(() => mapTrailToBreadcrumbItems(trail), [trail]);

	// go/jfe-eslint

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(onMount, []);

	return (
		<div css={breadcrumbsWrapperStyles}>
			<BreadcrumbsApp isVisible items={items} />
		</div>
	);
};

/**
 * This exists to let consumers render something consistent in SSR and initial CSR render for hydration, then switch
 * to the usual breadcrumbs which rely on components being mounted to populate the trail.
 */
export const EmptyBreadcrumbs = (): JSX.Element => {
	return (
		<div css={breadcrumbsWrapperStyles}>
			<BreadcrumbsApp isVisible items={[]} />
		</div>
	);
};

// @ts-expect-error - TS2322: value not assignable to declared type (iso-decl artefact)
const defaultExport: React.FC<
	Omit<
		{
			TenantContextSubscriber: typeof TenantContextSubscriberType;
			onMount: (args_0: UIAnalyticsEvent) => void;
		},
		keyof WithAnalyticsEventsProps
	>
> = ComponentWithAnalytics('breadcrumbsRenderer', { onMount: 'mounted' })(BreadcrumbRenderer);
export default defaultExport;

const breadcrumbsWrapperStyles = css({
	minHeight: '24px',
});
